import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { MICROSOFT_GRAPH_API_URL } from 'src/config';
import type {
  IMgtBaseResponse,
  IMgtFileResponse,
  IMgtFolderResponse,
  IMgtSiteContentResponse,
  IMgtSiteResponse
} from 'src/content/Documents/ResourceProviders/CommonUtils/types';
import { getAdToken } from 'src/keycloak';
import { getUnEncryptedQueryString } from 'src/services/utils';

export type MicrosoftQueryArg =
  | { type: 'getAllSites'; search?: string }
  | { type: 'getSites'; siteId: string; search?: string }
  | { type: 'getDrives'; siteId: string }
  | { type: 'getDriveItems'; siteId: string; driveId: string }
  | { type: 'getFolderItems'; siteId: string; driveId: string; itemId: string }
  | {
      type: 'getOneDriveItems';
      userId: string;
      fetchType?: string;
      itemId?: string;
      driveId?: string;
    };

export type MicrosoftQueryResponse =
  | IMgtBaseResponse<IMgtSiteResponse>
  | IMgtBaseResponse<IMgtSiteContentResponse>
  | IMgtBaseResponse<IMgtFolderResponse | IMgtFileResponse>;

function buildMicrosoftUrl(arg: MicrosoftQueryArg): { url: string } {
  switch (arg.type) {
    case 'getAllSites':
      return {
        url: `sites?${getUnEncryptedQueryString({ search: arg.search ?? '*' })}`
      };

    case 'getSites':
      return {
        url: arg.search
          ? `sites?${getUnEncryptedQueryString({ search: arg.search })}`
          : `sites/${arg.siteId}/sites`
      };

    case 'getDrives':
      return {
        url: `sites/${arg.siteId}/drives`
      };

    case 'getDriveItems':
      return {
        url: `sites/${arg.siteId}/drives/${arg.driveId}/root/children`
      };

    case 'getFolderItems':
      return {
        url: `sites/${arg.siteId}/drives/${arg.driveId}/items/${arg.itemId}/children`
      };

    case 'getOneDriveItems': {
      const { userId, fetchType, itemId, driveId } = arg;

      if (fetchType === '1') {
        return { url: `users/${userId}/drive/root/children` };
      }
      if (fetchType === '2') {
        return { url: `users/${userId}/drive/sharedWithMe` };
      }
      if (itemId) {
        const base = driveId
          ? `users/${userId}/drives/${driveId}`
          : `users/${userId}/drive`;
        return { url: `${base}/items/${itemId}/children` };
      }

      return { url: '' };
    }
  }
}

export const nmicrosoftApi = createApi({
  reducerPath: 'nmicrosoftApi',
  baseQuery: fetchBaseQuery({
    baseUrl: MICROSOFT_GRAPH_API_URL,
    prepareHeaders: (headers) => {
      const token = getAdToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getMicrosoftResource: builder.query<
      MicrosoftQueryResponse,
      MicrosoftQueryArg
    >({
      query: (arg) => buildMicrosoftUrl(arg)
    })
  })
});

export const { useGetMicrosoftResourceQuery } = nmicrosoftApi;
